import React, { useState, useEffect } from 'react'
import { Modal, Table, Alert, Spinner, Badge, Form, Row, Col, Button, ButtonGroup, Card } from 'react-bootstrap'
import {
  useAdminMunicipalityBucketFileSample,
  useAdminMunicipalityBucketFileQuery
} from 'api/hooks/use-admin-municipality-bucket-files'

const SEPARATOR_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'comma', label: 'Comma (,)' },
  { value: 'tab', label: 'Tab' },
  { value: 'pipe', label: 'Pipe (|)' },
  { value: 'semicolon', label: 'Semicolon (;)' }
]

const OPERATOR_OPTIONS = [
  { value: '=', label: '= (exact match)' },
  { value: '!=', label: '!= (not equal)' },
  { value: 'LIKE', label: 'contains (case-insensitive)' },
  { value: 'NOT LIKE', label: 'does not contain (case-insensitive)' },
  { value: '<', label: '<' },
  { value: '<=', label: '≤' },
  { value: '>', label: '>' },
  { value: '>=', label: '≥' }
]

const DEFAULT_ROW_COUNT = 50
const MIN_ROWS = 1
const MAX_ROWS = 1000
const WILDCARD_OPS = new Set(['LIKE', 'NOT LIKE'])

const emptyCondition = () => ({ column: '', operator: '=', value: '' })
const emptyGroup = (conjunction = 'AND') => ({ conjunction, conditions: [emptyCondition()] })

const formatBytes = (bytes) => {
  if (bytes == null) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

const ConjunctionToggle = ({ value, onChange, label }) => (
  <ButtonGroup size="sm" aria-label={label}>
    <Button
      variant={value === 'AND' ? 'primary' : 'light'}
      className={value === 'AND' ? '' : 'text-primary'}
      onClick={() => onChange('AND')}
    >
      AND
    </Button>
    <Button
      variant={value === 'OR' ? 'primary' : 'light'}
      className={value === 'OR' ? '' : 'text-primary'}
      onClick={() => onChange('OR')}
    >
      OR
    </Button>
  </ButtonGroup>
)

const InlineRowCount = ({ value, onCommit, disabled }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value))

  useEffect(() => {
    if (!editing) setDraft(String(value))
  }, [value, editing])

  const commit = () => {
    const n = parseInt(draft, 10)
    if (Number.isFinite(n) && n >= MIN_ROWS && n <= MAX_ROWS && n !== value) {
      onCommit(n)
    } else {
      setDraft(String(value))
    }
    setEditing(false)
  }

  if (editing) {
    return (
      <Form.Control
        type="number"
        size="sm"
        min={MIN_ROWS}
        max={MAX_ROWS}
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit() }
          if (e.key === 'Escape') { setDraft(String(value)); setEditing(false) }
        }}
        style={{ display: 'inline-block', width: '5rem' }}
        disabled={disabled}
      />
    )
  }

  return (
    <button
      type="button"
      className="btn btn-link p-0 align-baseline"
      style={{ textDecoration: 'underline dotted' }}
      onClick={() => setEditing(true)}
      disabled={disabled}
      aria-label="Change row count"
    >
      {value}
    </button>
  )
}

const ResultsTable = ({ headers, rows }) => (
  <Table striped bordered hover size="sm" responsive>
    <thead>
      <tr>
        {headers.map((h, i) => <th key={`${h}-${i}`}>{h}</th>)}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, idx) => (
        <tr key={idx}>
          {headers.map((h, i) => (
            <td key={`${h}-${i}`} style={{ whiteSpace: 'nowrap', maxWidth: '20rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row[h] ?? ''}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </Table>
)

const ConditionRow = ({ condition, conditionIndex, headers, onChange, onRemove, canRemove }) => {
  const isWildcardOp = WILDCARD_OPS.has(condition.operator)
  return (
    <Row className="g-2 align-items-end mb-2">
      <Col md={3}>
        <Form.Group>
          {conditionIndex === 0 && <Form.Label className="small mb-1">Column</Form.Label>}
          <Form.Select
            size="sm"
            value={condition.column}
            onChange={(e) => onChange({ column: e.target.value })}
            required
          >
            <option value="">— Select —</option>
            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
          </Form.Select>
        </Form.Group>
      </Col>
      <Col md={3}>
        <Form.Group>
          {conditionIndex === 0 && <Form.Label className="small mb-1">Operator</Form.Label>}
          <Form.Select
            size="sm"
            value={condition.operator}
            onChange={(e) => onChange({ operator: e.target.value })}
          >
            {OPERATOR_OPTIONS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
          </Form.Select>
        </Form.Group>
      </Col>
      <Col md={5}>
        <Form.Group>
          {conditionIndex === 0 && <Form.Label className="small mb-1">Value</Form.Label>}
          <Form.Control
            size="sm"
            type="text"
            value={condition.value}
            onChange={(e) => onChange({ value: e.target.value })}
            placeholder={isWildcardOp ? 'e.g. 16 (matches anywhere); include % for an explicit wildcard pattern' : ''}
          />
        </Form.Group>
      </Col>
      <Col md={1} className="text-end">
        {canRemove && (
          <Button
            variant="light"
            className="text-danger"
            size="sm"
            onClick={onRemove}
            aria-label="Remove condition"
          >
            ×
          </Button>
        )}
      </Col>
    </Row>
  )
}

const GroupCard = ({ group, groupIndex, headers, totalGroups, onUpdate, onRemove }) => {
  const updateCondition = (idx, patch) => {
    onUpdate({
      ...group,
      conditions: group.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c))
    })
  }
  const addCondition = () => onUpdate({ ...group, conditions: [...group.conditions, emptyCondition()] })
  const removeCondition = (idx) => onUpdate({ ...group, conditions: group.conditions.filter((_, i) => i !== idx) })

  return (
    <Card className="mb-2">
      <Card.Body className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center gap-2">
            <Badge bg="light" text="primary">Group {groupIndex + 1}</Badge>
            {group.conditions.length > 1 && (
              <>
                <span className="small text-muted">Conditions joined by</span>
                <ConjunctionToggle
                  value={group.conjunction}
                  onChange={(v) => onUpdate({ ...group, conjunction: v })}
                  label={`Group ${groupIndex + 1} conjunction`}
                />
              </>
            )}
          </div>
          {totalGroups > 1 && (
            <Button
              variant="light"
              className="text-danger"
              size="sm"
              onClick={onRemove}
              aria-label="Remove group"
            >
              Remove group
            </Button>
          )}
        </div>
        {group.conditions.map((cond, idx) => (
          <ConditionRow
            key={idx}
            condition={cond}
            conditionIndex={idx}
            headers={headers}
            onChange={(patch) => updateCondition(idx, patch)}
            onRemove={() => removeCondition(idx)}
            canRemove={group.conditions.length > 1}
          />
        ))}
        <Button
          type="button"
          size="sm"
          variant="light"
          className="text-primary"
          onClick={addCondition}
        >
          + Add condition
        </Button>
      </Card.Body>
    </Card>
  )
}

const BucketFileSampleModal = ({ municipalityId, bucket, fileKey, onClose }) => {
  const isOpen = !!fileKey
  const [mode, setMode] = useState('sample')
  const [separator, setSeparator] = useState('')
  const [rowCount, setRowCount] = useState(DEFAULT_ROW_COUNT)

  const [pendingGroups, setPendingGroups] = useState([emptyGroup()])
  const [outerConjunction, setOuterConjunction] = useState('AND')
  const [submittedState, setSubmittedState] = useState(null)

  useEffect(() => {
    if (isOpen) {
      setMode('sample')
      setSeparator('')
      setRowCount(DEFAULT_ROW_COUNT)
      setPendingGroups([emptyGroup()])
      setOuterConjunction('AND')
      setSubmittedState(null)
    }
  }, [fileKey, isOpen])

  const sampleQuery = useAdminMunicipalityBucketFileSample({
    municipalityId,
    bucket,
    fileKey,
    rows: rowCount,
    separator,
    enabled: isOpen
  })

  const queryQuery = useAdminMunicipalityBucketFileQuery({
    municipalityId,
    bucket,
    fileKey,
    // All query params are frozen into submittedState at Run time — editing
    // the inline row count or delimiter dropdown in filter mode doesn't
    // silently re-fire an S3 Select scan. Users see "results stale, re-run"
    // affordance instead (the Run button stays available).
    groups: submittedState?.groups || [],
    outerConjunction: submittedState?.outerConjunction || 'AND',
    limit: submittedState?.limit || DEFAULT_ROW_COUNT,
    separator: submittedState?.separator || '',
    enabled: isOpen && mode === 'query' && !!submittedState
  })

  const headers = sampleQuery.data?.headers || []
  const oneColumnWarning = sampleQuery.data && headers.length === 1 && !separator

  const updateGroup = (idx, next) => {
    setPendingGroups((prev) => prev.map((g, i) => (i === idx ? next : g)))
  }
  const addGroup = () => setPendingGroups((prev) => [...prev, emptyGroup()])
  const removeGroup = (idx) => setPendingGroups((prev) => prev.filter((_, i) => i !== idx))

  const allValid = pendingGroups.every((g) =>
    g.conditions.length > 0 && g.conditions.every((c) => c.column && c.operator)
  )

  const handleSubmitQuery = (e) => {
    e.preventDefault()
    if (!allValid) return
    setSubmittedState({
      groups: pendingGroups,
      outerConjunction,
      limit: rowCount,
      separator
    })
  }

  // Has the user changed anything since the last Run?
  const filterStale = !!submittedState && (
    submittedState.limit !== rowCount ||
    submittedState.separator !== separator ||
    submittedState.outerConjunction !== outerConjunction ||
    JSON.stringify(submittedState.groups) !== JSON.stringify(pendingGroups)
  )

  return (
    <Modal show={isOpen} onHide={onClose} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="text-truncate">
          <span className="text-muted small me-2">{bucket}:</span>
          <code>{fileKey}</code>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-2 align-items-end mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label className="small mb-1">Delimiter</Form.Label>
              <Form.Select size="sm" value={separator} onChange={(e) => setSeparator(e.target.value)}>
                {SEPARATOR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="small mb-1">Mode</Form.Label>
              <div>
                <ButtonGroup size="sm">
                  <Button
                    variant={mode === 'sample' ? 'primary' : 'light'}
                    className={mode === 'sample' ? '' : 'text-primary'}
                    onClick={() => setMode('sample')}
                  >
                    Sample first rows
                  </Button>
                  <Button
                    variant={mode === 'query' ? 'primary' : 'light'}
                    className={mode === 'query' ? '' : 'text-primary'}
                    onClick={() => setMode('query')}
                    disabled={headers.length === 0}
                  >
                    Filter
                  </Button>
                </ButtonGroup>
              </div>
            </Form.Group>
          </Col>
          {!separator && sampleQuery.data?.separator && (
            <Col md={4}>
              <div className="small text-muted">
                Auto-detected <Badge bg="light" text="primary">{sampleQuery.data.separator}</Badge> delimiter
              </div>
            </Col>
          )}
        </Row>

        {mode === 'query' && (
          <Form onSubmit={handleSubmitQuery} className="mb-3 pb-3 border-bottom">
            {pendingGroups.map((group, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && (
                  <div className="d-flex align-items-center justify-content-center my-2">
                    <span className="small text-muted me-2">Groups joined by</span>
                    <ConjunctionToggle
                      value={outerConjunction}
                      onChange={setOuterConjunction}
                      label="Outer conjunction"
                    />
                  </div>
                )}
                <GroupCard
                  group={group}
                  groupIndex={idx}
                  headers={headers}
                  totalGroups={pendingGroups.length}
                  onUpdate={(next) => updateGroup(idx, next)}
                  onRemove={() => removeGroup(idx)}
                />
              </React.Fragment>
            ))}
            <div className="d-flex justify-content-between align-items-center mt-2">
              <Button
                type="button"
                size="sm"
                variant="light"
                className="text-primary"
                onClick={addGroup}
              >
                + Add group
              </Button>
              <div className="d-flex align-items-center gap-2">
                {filterStale && (
                  <Badge bg="warning" text="dark">Stale — re-run</Badge>
                )}
                <Button
                  type="submit"
                  size="sm"
                  variant="primary"
                  disabled={queryQuery.isFetching || !allValid}
                >
                  Run
                </Button>
              </div>
            </div>
            <Form.Text className="text-muted d-block mt-2">
              S3 Select runs the WHERE clause server-side. Each group's conditions are joined by the
              group's AND/OR; groups are joined by the outer AND/OR. <code>contains</code> /{' '}
              <code>does not contain</code> are case-insensitive and wrap your input with <code>%</code>{' '}
              wildcards automatically. For <code>{'<'}</code> / <code>{'≤'}</code> / <code>{'>'}</code> /{' '}
              <code>{'≥'}</code>, numeric values trigger a numeric (FLOAT) cast — non-numeric column cells
              drop out of the result. <code>=</code> / <code>!=</code> are case-sensitive string compares.
              Match cap is the shared row count above.
            </Form.Text>
          </Form>
        )}

        {mode === 'sample' && (
          <>
            {sampleQuery.isLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading sample…</span>
                </Spinner>
              </div>
            )}
            {sampleQuery.error && (
              <Alert variant="danger">
                {sampleQuery.error.response?.data?.message || sampleQuery.error.message || 'Failed to load sample'}
              </Alert>
            )}
            {oneColumnWarning && (
              <Alert variant="warning" className="py-2 small">
                Only one column was detected. Try changing the Delimiter above.
              </Alert>
            )}
            {sampleQuery.data && !sampleQuery.isLoading && (
              <>
                <div className="mb-2 small text-muted d-flex align-items-center gap-2 flex-wrap">
                  <span>Showing</span>
                  <InlineRowCount
                    value={rowCount}
                    onCommit={setRowCount}
                    disabled={sampleQuery.isFetching}
                  />
                  <span>rows</span>
                  {sampleQuery.data.truncated && (
                    <Badge bg="light" text="primary">Sample fetch truncated</Badge>
                  )}
                </div>
                {headers.length > 0 ? (
                  <ResultsTable headers={headers} rows={sampleQuery.data.rows} />
                ) : (
                  <Alert variant="info" className="mb-0">No rows found in sample.</Alert>
                )}
              </>
            )}
          </>
        )}

        {mode === 'query' && (
          <>
            {!submittedState && (
              <Alert variant="info" className="mb-0">
                Build one or more condition groups above and click Run to search this file.
              </Alert>
            )}
            {submittedState && queryQuery.isFetching && (
              <div className="text-center py-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Querying…</span>
                </Spinner>
              </div>
            )}
            {submittedState && queryQuery.error && (
              <Alert variant="danger">
                {queryQuery.error.response?.data?.message || queryQuery.error.message || 'Query failed'}
              </Alert>
            )}
            {submittedState && queryQuery.data && !queryQuery.isFetching && (
              <>
                <div className="mb-2 small text-muted d-flex align-items-center gap-2 flex-wrap">
                  <span>Showing</span>
                  <span className="fw-semibold">{queryQuery.data.row_count}</span>
                  <span>matches · cap</span>
                  <InlineRowCount
                    value={rowCount}
                    onCommit={setRowCount}
                    disabled={queryQuery.isFetching}
                  />
                  {queryQuery.data.limit_reached && (
                    <Badge bg="warning" text="dark">Cap reached</Badge>
                  )}
                  {queryQuery.data.bytes_scanned > 0 && (
                    <span>· Scanned {formatBytes(queryQuery.data.bytes_scanned)}</span>
                  )}
                </div>
                {queryQuery.data.row_count > 0 ? (
                  <ResultsTable headers={headers} rows={queryQuery.data.rows} />
                ) : (
                  <Alert variant="info" className="mb-0">No rows matched.</Alert>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default BucketFileSampleModal
